import Group from 'types/Locations/Group';
import Location from 'types/Locations/Location';
import { TimeFrequency } from 'types/Meteorology/Fields/TrackingForecast/DetailedView/getTimeFrequencies';
import { Model } from 'types/Meteorology/Fields/TrackingForecast/DetailedView/model';
import { Variable } from 'types/Meteorology/Fields/TrackingForecast/DetailedView/variable';
import { Member } from 'types/Meteorology/Fields/TrackingForecast/DetailedView/member';

// Defaults ao abrir a Visão Detalhada (#1822, Área 3). Decisão da Bruna/Luiz:
// variável = Precipitação · frequência = Diário · modelo = INDICACAOMET ·
// data de previsão = a mais recente · Multi-Locais = TODOS os grupos e locais.
export const DEFAULT_TIME_FREQUENCY = 'd';
export const DEFAULT_MODEL_NAME = 'INDICACAOMET';
export const DEFAULT_VARIABLE = 'prec'; // Precipitação

export function resolveTimeFrequency(list: TimeFrequency[]): string | undefined {
  return list.find((tf) => tf.value === DEFAULT_TIME_FREQUENCY)?.value;
}

export function resolveModelId(list: Model[]): number | undefined {
  return list.find((m) => m.value === DEFAULT_MODEL_NAME)?.id;
}

// Fallback de modelo para Multi-Membros (#1822). INDICACAOMET é uma indicação
// determinística — não tem membros de ensemble, então o backend NÃO o inclui na
// lista de modelos desse modo. Sem fallback, `resolveModelId` devolvia undefined,
// o modelo nunca era selecionado e a cascata travava (sem modelo não há query de
// membros nem consulta). Quando o default preferido não está disponível, cai no
// primeiro modelo da lista.
export function firstModelId(list: Model[]): number | undefined {
  return list[0]?.id;
}

export function resolveVariable(list: Variable[]): string | undefined {
  return list.find((v) => v.value === DEFAULT_VARIABLE)?.value;
}

// Datas ISO ('YYYY-MM-DD'): a mais recente = o maior valor (sort lexicográfico
// funciona pro formato ISO). Independe da ordem em que a API devolve.
export function mostRecentDate(list: string[]): string | undefined {
  if (list.length === 0) return undefined;
  const sorted = [...list].sort();
  return sorted[sorted.length - 1];
}

// Default de data para Multi-Rodadas e Multi-Membros (#1822). O endpoint
// `data-previsao` desses modos lista a rodada de HOJE assim que ela começa, mas o
// gráfico dela só existe depois de processada — abrir na mais recente devolvia
// `data: []`. Regra da Bruna: quando a mais recente é hoje, cai pra data disponível
// anterior (D-1 = a rodada anterior da lista) e consulta. Só muda o comportamento
// quando hoje é a mais recente; nos demais casos é igual ao `mostRecentDate`.
export function mostRecentAvailableDate(
  list: string[],
  today: string,
): string | undefined {
  if (list.length === 0) return undefined;
  const sorted = [...list].sort();
  const newest = sorted[sorted.length - 1];
  if (newest === today && sorted.length > 1) {
    return sorted[sorted.length - 2];
  }
  return newest;
}

export function allGroupIds(groups: Group[]): number[] {
  return groups.map((g) => g.id);
}

export function allLocationIds(locations: Location[]): number[] {
  return locations.map((l) => l.id);
}

export function firstGroupId(groups: Group[]): number | undefined {
  return groups[0]?.id;
}

export function firstLocationOfFirstGroup(
  groups: Group[],
  locations: Location[],
): Location | undefined {
  const first = groups[0];
  if (!first) return undefined;
  return locations.find((l) => l.groupId === first.id);
}

export function allMemberIds(list: Member[]): number[] {
  return list.map((m) => m.id);
}

// "Pristine" = o modo ainda está no estado inicial do reducer (ou pós-resetCP),
// ou seja, o usuário não escolheu nada. Só nesse caso o auto-preenchimento pode
// rodar — ele nunca sobrescreve seleção do usuário nem preferência já aplicada.
export interface PristineFields {
  locationId?: number;
  multilocationIds?: number[];
  multimodelIds?: number[];
  members?: number[];
  rounds?: string[];
  variable?: string;
  forecastDate?: string;
}

// Só o campo de seleção PRÓPRIO do modo conta.
//
// Campos compartilhados (variable, forecastDate, timeFrequency, locationId) são
// preenchidos pela cascata de QUALQUER modo e sobrevivem à troca de aba — trocar
// de aba só despacha `setSubpage`, nunca `resetCP`. Enquanto eles entravam nesta
// conta, quem abrisse em Multi-Locais (a aba inicial) já chegava em Multi-Membros/
// Modelos/Rodadas com `variable` preenchida: o modo nascia "sujo", a cascata não
// rodava e a consulta não saía sozinha.
//
// Não há risco de sobrescrever escolha do usuário: cada efeito da cascata só
// preenche o campo que ainda está vazio, e preferência salva (filtrada por aba em
// useListPreferences) continua tendo precedência sobre o default.
const KEY_FIELDS_BY_SUBPAGE: Record<string, (keyof PristineFields)[]> = {
  locations: ['multilocationIds'],
  members: ['members'],
  models: ['multimodelIds'],
  rounds: ['rounds'],
};

function isEmptyField(value: PristineFields[keyof PristineFields]): boolean {
  if (value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  return value === '';
}

export function isPristine(fields: PristineFields, subpage: string): boolean {
  const keyFields = KEY_FIELDS_BY_SUBPAGE[subpage];
  if (!keyFields) return false;
  return keyFields.every((key) => isEmptyField(fields[key]));
}
